import React, { useState, useCallback, MouseEventHandler } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from 'components/system/layouts/Layout';
import redirectLinks from 'constants/redirectLinks';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ScreenContext from 'Contexts/Common/ScreenContext';
import COMMON from 'constants/common';
import ReturnButton from 'components/system/atoms/buttons/ReturnButton';
import MachineConfContent from 'components/machineConf/organisms/contents/MachineConfContent';
import DoneButton from 'components/system/atoms/buttons/DoneButton';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import { setAreaConf } from 'redux/slices/machineConfSlice';
import arrowIcon from 'assets/images/arrowIcon.svg';
import { AreaConfType, AreaSelectType, LocationType } from 'types/machineConf/machineConfType';
import AreaConfPanel from 'components/machineConf/organisms/Area/AreaConfPanel';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';
import { resetDescentLimitValue } from 'utils/resetDescentLimit.helper';
import MESSAGES from 'constants/messages';
import { InfoDialog } from 'components/system/organisms';

/* ************ Context ************ */
const ScreenContextValue = {
  title: 'エリア設定',
  screenId: 'SCR326',
  currentMenu: COMMON.MENU.MACHINE_CONF,
  disableContents: true,
};

/* ********** Constants ********** */
const AREA_SELECT_LIST = ['ベースエリア', 'エリア緑', 'エリア紫', 'エリア黄', 'エリア赤', 'エリア青'];
const INITIAL_LINE = '000000';

/* ************ Style ************ */
const useStyles = makeStyles()((theme) => ({
  content: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    margin: '18px 24px',
    minHeight: '563px',
    minWidth: '1080px',
  },
  baseArea: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '8px',
    borderColor: theme.base.tableBorder,
  },
  baseAreaSelect: {
    '& .area0': {
      backgroundColor: theme.base.area0,
      fontSize: 18,

      '&:hover': {
        backgroundColor: theme.base.area0,
      },
    },
    '& .area1': {
      backgroundColor: theme.base.area1,
      fontSize: 18,

      '&:hover': {
        backgroundColor: theme.base.area1,
      },
    },
    '& .area2': {
      backgroundColor: theme.base.area2,
      fontSize: 18,

      '&:hover': {
        backgroundColor: theme.base.area2,
      },
    },
    '& .area3': {
      backgroundColor: theme.base.area3,
      fontSize: 18,

      '&:hover': {
        backgroundColor: theme.base.area3,
      },
    },
    '& .area4': {
      backgroundColor: theme.base.area4,
      fontSize: 18,

      '&:hover': {
        backgroundColor: theme.base.area4,
      },
    },
    '& .area5': {
      backgroundColor: theme.base.area5,
      fontSize: 18,

      '&:hover': {
        backgroundColor: theme.base.area5,
      },
    },
  },
  topArea: {
    display: 'flex',
    height: '104px',
    '& .returnButton': {},
  },
  mainArea: {
    display: 'flex',
    height: '460px',
    width: '1080px',
    '& .areaSelectArea': {
      position: 'absolute',
      top: '142px',
      left: '0px',
      height: '424px',
      width: '344px',
      '& .description': {
        fontSize: '22px',
        height: '32px',
      },
      '& .areaSelectList': {
        height: '390px',
        width: '320px',
        padding: '24px 0px 0px 16px',
        '& .areaSelectButton': {
          height: '48px',
          width: '178px',
          boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
          },
        },
        '& .selected0': {
          height: '48px',
          width: '178px',
          border: '4px solid #707070',
        },
        '& .selected1': {
          height: '48px',
          width: '178px',
          border: '4px solid #08C57E',
        },
        '& .selected2': {
          height: '48px',
          width: '178px',
          border: '4px solid #991399',
        },
        '& .selected3': {
          height: '48px',
          width: '178px',
          border: '4px solid #FF960D',
        },
        '& .selected4': {
          height: '48px',
          width: '178px',
          border: '4px solid #FF566C',
        },
        '& .selected5': {
          height: '48px',
          width: '178px',
          border: '4px solid rgba(6,87,255,0.7)',
        },
        '& .clearButton': {
          height: '40px',
          width: '86px',
          margin: '4px 0px 0px 22px',
          boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
          },
        },
        '& .allClearButton': {
          height: '40px',
          width: '86px',
          margin: '4px 0px 0px 22px',
          boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
          lineHeight: '1.3',
          '&:hover': {
            boxShadow: '5px 5px 3px 0px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    '& .arrowIconRight': {
      position: 'absolute',
      top: '224px',
      left: '364px',
      height: '136px',
      width: '72px',
      transform: 'rotate(90deg)',
    },
    '& .areaConfArea': {
      position: 'absolute',
      top: '112px',
      left: '484px',
      height: '446px',
      width: '532px',
      '& .caption': {
        fontSize: '18px',
        height: '54px',
      },
      '& .areaConf': {
        marginTop: '-48px',
        marginLeft: '-52px',
        height: '470px',
        width: '470px',
      },
    },
    '& .doneArea': {
      position: 'absolute',
      top: '464px',
      left: '900px',
    },
  },
  areaSelect: {
    height: '48px',
    width: '290px',
    marginBottom: '12px',
    '& .area': {
      width: '178px',
    },
    '& .clear': {
      width: '86px',
    },
  },
}));

/* ********** local function ********** */
// エリア設定の行xに対し、設定済みの行情報を初期化する。
// areaValue='0'の場合は、一括クリア
const clearAreaLine = (line = '', areaValue: string) => {
  if (areaValue === COMMON.AREA.BASE) {
    return INITIAL_LINE;
  }
  return line.replaceAll(areaValue, COMMON.AREA.BASE);
};

/* ********** Sub Component ********** */
/**
 * AreaSelect エリア選択
 *
 * @author atsushi.teruya
 * @param
 * @returns {React.FC} - エリア選択
 *
 */
const AreaSelect: React.FC<React.PropsWithChildren<AreaSelectType>> = ({ index, caption, selected, onClickArea, onClickClear }) => {
  const { classes } = useStyles();

  const indexStr = String(index);
  return (
    <Box className={classes.areaSelect}>
      <NormalButton
        className={`${selected ? `selected${index}` : 'areaSelectButton'} area${indexStr} ${
          index <= 0 && classes.baseArea
        }`}
        onClick={onClickArea(indexStr)}>
        {caption}
      </NormalButton>
      {index === 0 ? (
        <NormalButton baseColor="black" className="allClearButton" onClick={onClickClear(indexStr)}>
          一括
          <br />
          クリア
        </NormalButton>
      ) : (
        <NormalButton baseColor="black" className="clearButton" onClick={onClickClear(indexStr)}>
          クリア
        </NormalButton>
      )}
    </Box>
  );
};

/* ******** Main Component ******** */
/**
 * エリア設定
 *
 * @author atsushi.teruya
 * @returns {React.FC} - エリア設定
 *
 */
const AreaConf: React.FC<React.PropsWithChildren<unknown>> = () => {
  /* ************ state/redux ************ */
  const dispatch = useAppDispatch();
  const areaConf = useAppSelector((state) => state.machineConf.machineConf.areaConf);
  const pitConf = useAppSelector((state) => state.machineConf.machineConf.pitConf);

  // Local
  const [inputValues, setInputValues] = useState({ selectedArea: COMMON.AREA.BASE });
  const [tempAreaConf, setTempAreaConf] = useState(areaConf);
  const [openConfirm, setOpenConfirm] = useState(false);

  // 初期処理 or 並べ替え変更時の処理

  /* ************ other component ************ */
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  /* ************ Event ************ */

  // return button change
  const handleClickReturn = () => {
    returnScreen();
  };

  const returnScreen = () => {
    const pathname = location.state
      ? (location.state as LocationType).fromScreen
      : redirectLinks.MCONF_STATION_CONF_AREA_POWER_CONF;
    navigate(pathname, {
      state: { fromScreen: redirectLinks.MCONF_STATION_CONF_AREA_CONF },
    });
  };

  // エリア
  const handleClickArea = (areaValue: string): MouseEventHandler<HTMLButtonElement> => () => {
    // 選択中エリアを更新
    setInputValues((prevState) => ({ ...prevState, selectedArea: areaValue }));
  };

  // クリア
  const handleClickClear = (areaValue: string): MouseEventHandler<HTMLButtonElement> => () => {
    setTempAreaConf((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          line1: clearAreaLine(prevState.line1, areaValue),
          line2: clearAreaLine(prevState.line2, areaValue),
          line3: clearAreaLine(prevState.line3, areaValue),
          line4: clearAreaLine(prevState.line4, areaValue),
          line5: clearAreaLine(prevState.line5, areaValue),
          line6: clearAreaLine(prevState.line6, areaValue),
        };
      }
      return prevState;
    });
  };

  // 確定
  const handleClickDone = async () => {
    const newTempAreaConf = resetDescentLimitValue(tempAreaConf);
    // validationチェック及びセッション更新
    const http = new HttpConnection({ dispatch });
    await http.post<AreaConfType>(REST_API.MACHINE_CONF.AREA_CONF_VALIDATION, tempAreaConf);

    if (!newTempAreaConf?.hasReset) {
      dispatch(setAreaConf(tempAreaConf));
      returnScreen();
    } else {
      const areaConfReset = newTempAreaConf.areaConf;
      await http.post(REST_API.MACHINE_CONF.SET_NO_CONF_WITH_AREA_CONF, {
        descentLimit1: areaConfReset.descentLimit1,
        descentLimit2: areaConfReset.descentLimit2,
        descentLimit3: areaConfReset.descentLimit3,
        descentLimit4: areaConfReset.descentLimit4,
        descentLimit5: areaConfReset.descentLimit5,
        descentLimit6: areaConfReset.descentLimit6,
      });
      setOpenConfirm(true);
      // reset DescentLimit
      dispatch(setAreaConf(areaConfReset));
    }
  };

  const onClickOkModeConfirm = () => {
    setOpenConfirm(false);
    returnScreen();
  };

  // 落とし穴変更時
  const handleChangeArea = useCallback((newAreaConf: AreaConfType) => {
    setTempAreaConf(newAreaConf);
  }, []);

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <MachineConfContent>
          <Box className={classes.content}>
            <Box className={classes.topArea}>
              {/* 戻るボタン */}
              <Box className="returnButton">
                <ReturnButton onClick={handleClickReturn}>
                  設定を反映 <br />
                  させずに戻る
                </ReturnButton>
              </Box>
            </Box>
            <Box className={classes.mainArea}>
              {/* エリア選択 */}
              <Box className="areaSelectArea">
                <p className="description">※最大6エリアまで指定できます。</p>
                <Box className={`${classes.baseArea} ${classes.baseAreaSelect} areaSelectList`}>
                  {AREA_SELECT_LIST.map((value, index) => (
                    <AreaSelect
                      key={String(index)}
                      index={index}
                      caption={value}
                      selected={inputValues.selectedArea === String(index)}
                      onClickArea={handleClickArea}
                      onClickClear={handleClickClear}
                    />
                  ))}
                </Box>
              </Box>
              <img src={arrowIcon} alt="arrowIcon" className="arrowIconRight" />
              {/* エリア設定 */}
              <Box className="areaConfArea">
                <p className="caption">
                  ①左のパネルから指定したいエリアの色を選択します。 <br />
                  ②指定したいエリアをタッチまたはスライドして選択します。
                </p>
                <Box className="areaConf">
                  <AreaConfPanel
                    areaValue={inputValues.selectedArea}
                    onChange={handleChangeArea}
                    areaConf={tempAreaConf}
                    pitConf={pitConf}
                  />
                </Box>
              </Box>
              {/* 確定ボタン */}
              <Box className="doneArea">
                <DoneButton onClick={handleClickDone}>確 定</DoneButton>
              </Box>
            </Box>
          </Box>
          <InfoDialog open={openConfirm} msg={MESSAGES.INF241} closeFunc={onClickOkModeConfirm} />
        </MachineConfContent>
      </Layout>
    </ScreenContext.Provider>
  );
};

export default AreaConf;
